import React, { useState, useEffect } from 'react';
import { Table, Tag, Space, Button, message, Tooltip, Spin, Empty, Card, Statistic, Row, Col, Calendar, Badge, Timeline, Tabs, DatePicker, Select, Typography } from 'antd';
import { 
  ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined, 
  ClockCircleOutlined, RightCircleOutlined, CalendarOutlined,
  HistoryOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import scheduledSopService from '../services/scheduledSopService';

const { Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

/**
 * 定时SOP步骤执行情况组件
 * 以时间线和日历方式展示定时SOP的执行情况
 */
const ScheduledSopStepExecutions = ({ templateId }) => {
  const [loading, setLoading] = useState(false);
  const [executionStats, setExecutionStats] = useState({
    totalExecutions: 0,
    completedExecutions: 0,
    inProgressExecutions: 0,
    failedExecutions: 0,
    pendingExecutions: 0,
    skippedExecutions: 0
  });
  const [executions, setExecutions] = useState([]);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [viewMode, setViewMode] = useState('timeline');
  const [dateRange, setDateRange] = useState([dayjs().subtract(7, 'day'), dayjs()]);
  const [statusFilter, setStatusFilter] = useState('ALL');

  // 获取定时SOP执行情况
  const fetchExecutions = async () => {
    try {
      if (!templateId) return;
      
      setLoading(true);
      
      // 获取租户ID
      const tenantStr = localStorage.getItem('tenant');
      let tenantId;
      try {
        if (tenantStr) {
          const tenant = JSON.parse(tenantStr);
          tenantId = tenant.id.toString();
        } else {
          throw new Error('找不到租户信息');
        }
      } catch (error) {
        console.error('解析tenant信息失败:', error);
        throw new Error('租户信息解析失败，无法继续操作');
      }
      
      // 准备请求参数
      const params = {
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD'),
        status: statusFilter !== 'ALL' ? statusFilter : undefined
      };
      
      // 调用API获取数据
      const response = await scheduledSopService.getScheduledSopExecutionRecords(templateId, tenantId, params);
      
      console.log('定时SOP执行情况响应:', response);
      
      if (!response || !response.executions) {
        setExecutions([]);
        updateExecutionStats([]);
        return;
      }
      
      // 处理数据
      const executionsData = response.executions;
      setExecutions(executionsData);
      
      // 更新统计数据
      updateExecutionStats(executionsData);
    } catch (error) {
      console.error('获取定时SOP执行情况失败:', error);
      message.error('获取执行情况失败: ' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  // 从执行记录中统计数据
  const updateExecutionStats = (executionsData) => {
    if (!executionsData || executionsData.length === 0) {
      setExecutionStats({
        totalExecutions: 0,
        completedExecutions: 0,
        inProgressExecutions: 0,
        failedExecutions: 0,
        pendingExecutions: 0,
        skippedExecutions: 0
      });
      return;
    }
    
    const stats = {
      totalExecutions: executionsData.length,
      completedExecutions: 0,
      inProgressExecutions: 0,
      failedExecutions: 0,
      pendingExecutions: 0,
      skippedExecutions: 0
    };
    
    executionsData.forEach(execution => {
      const status = execution.status ? execution.status.toUpperCase() : '';
      
      if (status === 'COMPLETED') {
        stats.completedExecutions++;
      } else if (status === 'IN_PROGRESS') {
        stats.inProgressExecutions++;
      } else if (status === 'FAILED') {
        stats.failedExecutions++;
      } else if (status === 'PENDING') {
        stats.pendingExecutions++;
      } else if (status === 'SKIPPED') {
        stats.skippedExecutions++;
      }
    });
    
    setExecutionStats(stats);
  };

  // 组件挂载或依赖项变化时获取数据
  useEffect(() => {
    if (templateId) {
      fetchExecutions();
    }
  }, [templateId, dateRange, statusFilter]);

  // 刷新数据
  const handleRefresh = () => {
    fetchExecutions();
  };

  // 处理日期选择变化
  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  // 处理日期范围变化
  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setDateRange(dates);
    }
  };

  // 处理状态筛选变化
  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
  };

  // 获取状态标签颜色
  const getStatusColor = (status) => {
    const statusMap = {
      'COMPLETED': 'success',
      'IN_PROGRESS': 'processing',
      'FAILED': 'error',
      'PENDING': 'warning',
      'SKIPPED': 'default'
    };
    return statusMap[status] || 'default';
  };

  // 渲染状态标签
  const renderStatus = (status) => {
    return <Tag color={getStatusColor(status)}>{status}</Tag>;
  };

  // 获取状态图标
  const getStatusIcon = (status) => {
    const iconMap = {
      'COMPLETED': <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      'IN_PROGRESS': <RightCircleOutlined style={{ color: '#1890ff' }} />,
      'FAILED': <CloseCircleOutlined style={{ color: '#f5222d' }} />,
      'PENDING': <ClockCircleOutlined style={{ color: '#faad14' }} />,
      'SKIPPED': <CloseCircleOutlined style={{ color: '#d9d9d9' }} />
    };
    return iconMap[status] || <ClockCircleOutlined style={{ color: '#d9d9d9' }} />;
  };

  // 格式化日期时间
  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return '未设置';
    const date = dayjs(dateTimeStr);
    return date.format('YYYY-MM-DD HH:mm:ss');
  };

  // 渲染时间线模式的执行情况
  const renderTimelineView = () => {
    const filteredExecutions = executions.filter(execution => {
      // 日期范围过滤
      const execDate = dayjs(execution.scheduledTime);
      return execDate.isAfter(dateRange[0]) && execDate.isBefore(dateRange[1].add(1, 'day'));
    });

    // 按日期分组
    const groupedByDate = {};
    filteredExecutions.forEach(execution => {
      const dateKey = dayjs(execution.scheduledTime).format('YYYY-MM-DD');
      if (!groupedByDate[dateKey]) {
        groupedByDate[dateKey] = [];
      }
      groupedByDate[dateKey].push(execution);
    });

    // 排序的日期键
    const sortedDateKeys = Object.keys(groupedByDate).sort((a, b) => 
      dayjs(b).diff(dayjs(a))
    );

    if (sortedDateKeys.length === 0) {
      return (
        <Empty description="没有找到执行记录" />
      );
    }

    return (
      <div className="timeline-container">
        {sortedDateKeys.map(dateKey => (
          <Card 
            key={dateKey} 
            title={
              <Space>
                <CalendarOutlined />
                {dayjs(dateKey).format('YYYY年MM月DD日')}
              </Space>
            }
            style={{ marginBottom: 16 }}
          >
            <Timeline mode="left">
              {groupedByDate[dateKey]
                .sort((a, b) => dayjs(a.scheduledTime).diff(dayjs(b.scheduledTime)))
                .map(execution => (
                  <Timeline.Item 
                    key={execution.id} 
                    color={getStatusColor(execution.status)}
                    dot={getStatusIcon(execution.status)}
                  >
                    <Card 
                      size="small" 
                      title={
                        <Space>
                          <Text strong>计划时间：{dayjs(execution.scheduledTime).format('HH:mm:ss')}</Text>
                          {renderStatus(execution.status)}
                        </Space>
                      }
                      style={{ marginBottom: 8 }}
                    >
                      <Row gutter={[16, 8]}>
                        <Col span={12}>
                          <Text>步骤名称：{execution.stepName || '未命名步骤'}</Text>
                        </Col>
                        <Col span={12}>
                          <Text>步骤类型：{execution.stepType || execution.actionType || '未知类型'}</Text>
                        </Col>
                        <Col span={12}>
                          <Text>开始时间：{formatDateTime(execution.startTime)}</Text>
                        </Col>
                        <Col span={12}>
                          <Text>完成时间：{formatDateTime(execution.completionTime)}</Text>
                        </Col>
                        {execution.errorMessage && (
                          <Col span={24}>
                            <Text type="danger">错误信息：{execution.errorMessage}</Text>
                          </Col>
                        )}
                        {execution.targetName && (
                          <Col span={24}>
                            <Text>目标对象：{execution.targetName}</Text>
                          </Col>
                        )}
                      </Row>
                    </Card>
                  </Timeline.Item>
                ))}
            </Timeline>
          </Card>
        ))}
      </div>
    );
  };

  // 渲染日历模式的执行情况
  const renderCalendarView = () => {
    // 日历单元格渲染函数
    const dateCellRender = (value) => {
      const dateStr = value.format('YYYY-MM-DD');
      
      // 查找当天的执行记录
      const dayExecutions = executions.filter(execution => 
        dayjs(execution.scheduledTime).format('YYYY-MM-DD') === dateStr
      );
      
      if (dayExecutions.length === 0) {
        return null;
      }
      
      // 统计状态
      const statusCount = {};
      dayExecutions.forEach(execution => {
        statusCount[execution.status] = (statusCount[execution.status] || 0) + 1;
      });
      
      return (
        <div className="calendar-events">
          {Object.entries(statusCount).map(([status, count]) => (
            <Badge 
              key={status} 
              status={getStatusColor(status)} 
              text={`${status} (${count})`} 
              style={{ display: 'block' }}
            />
          ))}
        </div>
      );
    };
    
    // 选中日期的执行详情
    const selectedDateExecutions = executions.filter(execution => 
      dayjs(execution.scheduledTime).format('YYYY-MM-DD') === selectedDate.format('YYYY-MM-DD')
    );
    
    return (
      <div className="calendar-view">
        <Row gutter={16}>
          <Col span={16}>
            <Card>
              <Calendar 
                value={selectedDate}
                onSelect={handleDateChange}
                dateCellRender={dateCellRender}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card 
              title={
                <Space>
                  <CalendarOutlined />
                  {selectedDate.format('YYYY年MM月DD日')}的执行情况
                </Space>
              }
            >
              {selectedDateExecutions.length > 0 ? (
                <Timeline>
                  {selectedDateExecutions
                    .sort((a, b) => dayjs(a.scheduledTime).diff(dayjs(b.scheduledTime)))
                    .map(execution => (
                      <Timeline.Item 
                        key={execution.id} 
                        color={getStatusColor(execution.status)}
                        dot={getStatusIcon(execution.status)}
                      >
                        <div>
                          <Text strong>
                            {dayjs(execution.scheduledTime).format('HH:mm:ss')} - {execution.stepName || '未命名步骤'}
                          </Text>
                          <div>{renderStatus(execution.status)}</div>
                          <div>
                            <Text type="secondary">
                              {execution.targetName ? `目标: ${execution.targetName}` : ''}
                            </Text>
                          </div>
                        </div>
                      </Timeline.Item>
                    ))}
                </Timeline>
              ) : (
                <Empty description="当日没有执行记录" />
              )}
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  // 渲染表格模式的执行情况
  const renderTableView = () => {
    const columns = [
      {
        title: '计划时间',
        dataIndex: 'scheduledTime',
        key: 'scheduledTime',
        render: text => formatDateTime(text),
        sorter: (a, b) => dayjs(a.scheduledTime).diff(dayjs(b.scheduledTime))
      },
      {
        title: '步骤名称',
        dataIndex: 'stepName',
        key: 'stepName'
      },
      {
        title: '目标对象',
        dataIndex: 'targetName',
        key: 'targetName'
      },
      {
        title: '状态',
        dataIndex: 'status',
        key: 'status',
        render: status => renderStatus(status),
        filters: [
          { text: '已完成', value: 'COMPLETED' },
          { text: '执行中', value: 'IN_PROGRESS' },
          { text: '失败', value: 'FAILED' },
          { text: '等待中', value: 'PENDING' },
          { text: '已跳过', value: 'SKIPPED' }
        ],
        onFilter: (value, record) => record.status === value
      },
      {
        title: '开始时间',
        dataIndex: 'startTime',
        key: 'startTime',
        render: text => formatDateTime(text)
      },
      {
        title: '完成时间',
        dataIndex: 'completionTime',
        key: 'completionTime',
        render: text => formatDateTime(text)
      },
      {
        title: '错误信息',
        dataIndex: 'errorMessage',
        key: 'errorMessage',
        render: text => text ? (
          <Tooltip title={text}>
            <Text type="danger" ellipsis style={{ maxWidth: 150 }}>
              {text}
            </Text>
          </Tooltip>
        ) : null
      }
    ];

    return (
      <Table 
        dataSource={executions.map(item => ({ ...item, key: item.id }))}
        columns={columns}
        rowKey="id"
        pagination={{ pageSize: 10 }}
        scroll={{ x: 'max-content' }}
      />
    );
  };

  return (
    <div className="scheduled-sop-executions">
      <div className="controls" style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col>
            <Space>
              <Button 
                type="primary" 
                icon={<ReloadOutlined />} 
                onClick={handleRefresh}
                loading={loading}
              >
                刷新
              </Button>
              
              <Select 
                value={viewMode} 
                onChange={setViewMode}
                style={{ width: 120 }}
              >
                <Option value="timeline">时间线视图</Option>
                <Option value="calendar">日历视图</Option>
                <Option value="table">表格视图</Option>
              </Select>
              
              <RangePicker 
                value={dateRange}
                onChange={handleDateRangeChange}
              />
              
              <Select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                style={{ width: 120 }}
              >
                <Option value="ALL">全部状态</Option>
                <Option value="COMPLETED">已完成</Option>
                <Option value="IN_PROGRESS">执行中</Option>
                <Option value="FAILED">失败</Option>
                <Option value="PENDING">等待中</Option>
                <Option value="SKIPPED">已跳过</Option>
              </Select>
            </Space>
          </Col>
        </Row>
      </div>
      
      <Card 
        title={
          <Space>
            <HistoryOutlined />
            执行情况统计
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <Row gutter={16}>
          <Col span={4}>
            <Statistic 
              title="总执行数" 
              value={executionStats.totalExecutions} 
              prefix={<HistoryOutlined />}
            />
          </Col>
          <Col span={4}>
            <Statistic 
              title="已完成" 
              value={executionStats.completedExecutions}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Col>
          <Col span={4}>
            <Statistic 
              title="执行中" 
              value={executionStats.inProgressExecutions}
              valueStyle={{ color: '#1890ff' }}
              prefix={<RightCircleOutlined />}
            />
          </Col>
          <Col span={4}>
            <Statistic 
              title="失败" 
              value={executionStats.failedExecutions}
              valueStyle={{ color: '#f5222d' }}
              prefix={<CloseCircleOutlined />}
            />
          </Col>
          <Col span={4}>
            <Statistic 
              title="等待中" 
              value={executionStats.pendingExecutions}
              valueStyle={{ color: '#faad14' }}
              prefix={<ClockCircleOutlined />}
            />
          </Col>
          <Col span={4}>
            <Statistic 
              title="已跳过" 
              value={executionStats.skippedExecutions}
              valueStyle={{ color: '#d9d9d9' }}
              prefix={<CloseCircleOutlined />}
            />
          </Col>
        </Row>
      </Card>
      
      <Spin spinning={loading}>
        {viewMode === 'timeline' && renderTimelineView()}
        {viewMode === 'calendar' && renderCalendarView()}
        {viewMode === 'table' && renderTableView()}
      </Spin>
    </div>
  );
};

export default ScheduledSopStepExecutions; 